import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const SERVICES = [
  {
    slug: 'roof-replacement',
    icon: 'ri-home-2-fill',
    title: 'Roof Replacement',
    subtitle: 'Full tear-off & GAF installation',
    desc: 'Complete system removal and installation using premium GAF materials — engineered from the decking up to protect your Gulf Coast property for decades.',
    tag: 'Most Requested',
  },
  {
    slug: 'roof-repairs',
    icon: 'ri-hammer-fill',
    title: 'Roof Repairs',
    subtitle: 'Fast, lasting leak & damage fixes',
    desc: 'Quick diagnosis and permanent repairs for leaks, storm-stripped shingles, and failing flashing — fixing root causes, not just symptoms.',
    tag: null,
  },
  {
    slug: 'storm-damage-restoration',
    icon: 'ri-thunderstorms-fill',
    title: 'Storm Damage Restoration',
    subtitle: 'Fortification-certified rebuild',
    desc: 'Emergency tarping through full Fortification-certified restoration — built to exceed pre-storm performance and pass insurance inspections.',
    tag: 'Hurricane Ready',
  },
  {
    slug: 'insurance-claim-assistance',
    icon: 'ri-file-shield-2-fill',
    title: 'Insurance Claims',
    subtitle: 'Full adjuster coordination',
    desc: 'Side-by-side adjuster support, thorough documentation, and full claim advocacy — ensuring you receive every dollar you\'re entitled to.',
    tag: null,
  },
  {
    slug: 'commercial-roofing',
    icon: 'ri-building-2-fill',
    title: 'Commercial Roofing',
    subtitle: 'TPO, bitumen & flat systems',
    desc: 'Expert commercial installation — TPO membrane, modified bitumen, and flat roofing systems with minimal operational disruption.',
    tag: null,
  },
  {
    slug: 'financing',
    icon: 'ri-bank-card-fill',
    title: 'Financing Available',
    subtitle: 'Flexible payment options',
    desc: 'A quality roof should never be out of reach. Ask about our flexible financing plans designed for homeowners across the Gulf Coast.',
    tag: null,
  },
];

export default function ServicesSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.05 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const scrollToEstimate = () => {
    document.getElementById('estimate')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="services" ref={ref} className="py-24 md:py-32 bg-[#141414]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">

        {/* Section header */}
        <div className={`mb-16 text-center transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="w-8 h-px bg-[#C9A961]"></span>
            <span className="text-[11px] font-bold tracking-widest uppercase text-[#C9A961]">Services Available</span>
            <span className="w-8 h-px bg-[#C9A961]"></span>
          </div>
          <h2 className="font-condensed font-bold text-5xl md:text-6xl text-white uppercase leading-none mb-4">
            Everything Your Roof Needs
          </h2>
          <p className="text-white/50 text-base leading-relaxed max-w-xl mx-auto font-light">
            From full replacements to emergency storm repairs — every service delivered with GAF-certified quality, total transparency, and zero pressure.
          </p>
        </div>

        {/* Services grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-14">
          {SERVICES.map((s, i) => (
            <div
              key={s.slug}
              className={`group relative border border-white/10 rounded-lg p-8 bg-white/5 hover:bg-white/8 hover:border-[#C9A961]/40 transition-all duration-400 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              {/* Top gold accent on hover */}
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-[#C9A961] rounded-t-lg scale-x-0 group-hover:scale-x-100 transition-transform duration-400 origin-left"></div>

              {/* Tag */}
              {s.tag && (
                <span className="absolute top-5 right-5 text-[9px] font-bold tracking-widest uppercase bg-[#C9A961]/15 text-[#C9A961] px-2.5 py-1 rounded-full">
                  {s.tag}
                </span>
              )}

              {/* Icon */}
              <div className="w-12 h-12 flex items-center justify-center bg-white/8 border border-white/12 rounded-lg mb-6 group-hover:border-[#C9A961]/50 group-hover:bg-[#C9A961]/10 transition-all duration-300">
                <i className={`${s.icon} text-[#C9A961] text-xl`}></i>
              </div>

              {/* Content */}
              <h3 className="font-condensed font-bold text-2xl text-white uppercase tracking-tight mb-1">
                {s.title}
              </h3>
              <p className="text-[#C9A961] text-[11px] font-semibold tracking-widest uppercase mb-3">
                {s.subtitle}
              </p>
              <p className="text-white/50 text-sm leading-relaxed font-light mb-6">
                {s.desc}
              </p>

              {/* CTA */}
              <Link
                to={s.slug !== 'financing' ? `/services/${s.slug}` : '#estimate'}
                onClick={s.slug === 'financing' ? (e) => { e.preventDefault(); scrollToEstimate(); } : undefined}
                className="inline-flex items-center gap-2 text-[11px] font-bold tracking-widest uppercase text-white/50 group-hover:text-[#C9A961] transition-colors cursor-pointer"
              >
                {s.slug === 'financing' ? 'Ask About Financing' : 'Learn More'}
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
              </Link>
            </div>
          ))}
        </div>

        {/* Bottom CTA bar */}
        <div className={`flex flex-col md:flex-row items-center justify-between gap-6 p-8 border border-white/10 rounded-lg bg-white/4 transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div>
            <p className="font-condensed font-bold text-2xl md:text-3xl text-white uppercase tracking-wide mb-1">
              Not Sure Which Service You Need?
            </p>
            <p className="text-white/40 text-sm font-light">
              Call us — we'll inspect your roof for free and walk you through your options honestly.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <button onClick={scrollToEstimate}
              className="inline-flex items-center justify-center gap-2 bg-[#C9A961] text-[#0A0A0A] font-bold text-[11px] tracking-widest uppercase px-8 py-3.5 rounded hover:bg-[#E2C97E] transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-calendar-check-line"></i>
              Free Estimate
            </button>
            <a href="tel:2285473887"
              className="inline-flex items-center justify-center gap-2 border border-white/15 text-white/70 font-semibold text-[11px] tracking-widest uppercase px-8 py-3.5 rounded hover:border-[#C9A961]/50 hover:text-[#C9A961] transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill text-[#C9A961]"></i>
              (228) 547-3887
            </a>
          </div>
        </div>

      </div>
    </section>
  );
}
